import { ProfileRole } from "@qendo/profile";
import type { ContactListingItem } from "@qendo/profile/contacts";
import type { InvitationListingItem } from "@qendo/profile/invitation";
import type { TeamListingRecord, TeamMemberListingItem } from "@qendo/profile/team";
import { useMemo, useState } from "react";
import { Button } from "~/components/Button";
import { FilteredList } from "~/components/layout/FilteredList";
import { Page } from "~/components/layout/Page";
import { SectionHeader } from "~/components/layout/SectionHeader";
import { TeamListingItem } from "~/components/layout/team/TeamListingItem";
import { Authorize } from "~/components/permission/Authorize";
import { Can } from "~/components/permission/Can";
import { Only } from "~/components/permission/Only";
import { useProfile } from "~/providers/ProfileProvider";

export default function CareTeamIndex() {
    const { invitations, refreshInvitations, team, refreshTeam, contacts, refreshContacts, profile_id } = useProfile();
    const [refreshing, setRefreshing] = useState(false);

    const items: TeamListingRecord[] = useMemo(
        () => [
            ...team
                .filter((member) => member.role !== ProfileRole.owner || member.user.profile_id !== profile_id)
                .map<TeamMemberListingItem>((value) => ({
                    id: value.id,
                    name: value.user.name,
                    avatar: value.user.avatar ?? null,
                    role: value.role,
                    invited: false,
                    user: value.user,
                    emergency_contact: value.emergency_contact,
                })),
            ...contacts
                .filter(({ role }) => role === ProfileRole.contact)
                .map<ContactListingItem>((value) => ({
                    id: value.id,
                    name: value.name,
                    role: value.role,
                    invited: false,
                    emergency_contact: value.emergency_contact,
                })),
            ...invitations.map<InvitationListingItem>((value) => ({
                id: value.id,
                name: value.name,
                created_at: value.created_at,
                role: value.role,
                invited: true,
                emergency_contact: value.emergency_contact,
            })),
        ],
        [invitations, contacts, team, profile_id],
    );

    const has_items = items.length > 0;

    const refresh = async () => {
        if (refreshing) {
            return;
        }
        setRefreshing(true);

        await Promise.allSettled([refreshInvitations(), refreshTeam(), refreshContacts()]).finally(() =>
            setRefreshing(false),
        );
    };

    return (
        <Authorize ability="view" section="team">
            <Page spacer refreshWith={refresh} isRefreshing={refreshing} scrollable={!has_items}>
                <SectionHeader
                    title="Care team"
                    icon="care-team"
                    button={() => (
                        <Can ability="update" section="team">
                            <Button title="Invite person" variant="primary" size="lg" href="/more/care-team/create" />
                        </Can>
                    )}
                >
                    Add people to your care team, like family and healthcare providers, to make sharing information and
                    health updates easy.
                </SectionHeader>
                <Only when={items.length}>
                    <FilteredList<TeamListingRecord>
                        data={items}
                        searchWith={(item, term) => item.name.toLowerCase().includes(term.toLowerCase())}
                        searchPlaceholder="Search by name"
                        refreshWith={refresh}
                        isRefreshing={refreshing}
                        renderItem={({ item }) => <TeamListingItem item={item} />}
                    />
                </Only>
            </Page>
        </Authorize>
    );
}
